import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, Repeat, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { DeadlineDialog } from "@/components/DeadlineDialog";
import { ProfileSelect, useProfiles, profileName } from "@/components/ProfileSelect";
import { api, errMsg, fmtDate, daysLeft, fmtEur, notifyDeadlines } from "@/lib/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import { it } from "date-fns/locale";

const Badge = ({ d }) => {
  const n = daysLeft(d.due_date);
  if (d.done) return <span className="text-xs rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1">Fatto</span>;
  const cls = n < 0 ? "bg-red-50 text-red-700" : n <= d.remind_days ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600";
  return <span className={`text-xs rounded-full px-2.5 py-1 ${cls}`}>{n < 0 ? `Scaduta da ${-n} gg` : n === 0 ? "Oggi" : `Tra ${n} gg`}</span>;
};

export default function Deadlines() {
  const profiles = useProfiles();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("open");
  const [edit, setEdit] = useState(null);
  const [day, setDay] = useState();

  const load = useCallback(() => {
    api.get("/deadlines", { params: filter === "all" ? {} : { profile_id: filter } }).then(({ data }) => setItems(data));
    notifyDeadlines();
  }, [filter]);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (d) => {
    await api.post(`/deadlines/${d.deadline_id}/toggle`);
    if (!d.done && d.recurrence !== "none") toast.success("Fatto! Creata la prossima scadenza ricorrente");
    load();
  };
  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.title}"?`)) return;
    await api.delete(`/deadlines/${d.deadline_id}`);
    toast.success("Scadenza eliminata");
    load();
  };
  const testEmail = async () => {
    try {
      await api.post("/reminders/test");
      toast.success("Email di riepilogo inviata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = useMemo(() => items.filter((d) => (tab === "open" ? !d.done : d.done)).filter((d) => !day || d.due_date === dayjs(day).format("YYYY-MM-DD")), [items, tab, day]);
  const dates = items.filter((d) => !d.done).map((d) => new Date(d.due_date + "T00:00:00"));

  return (
    <div data-testid="deadlines-page">
      <PageHeader eyebrow="Scadenzario globale" title="Scadenze e promemoria" desc="Ti avvisiamo in app e via email prima di ogni scadenza.">
        <Button variant="outline" className="rounded-full" onClick={testEmail} data-testid="deadlines-test-email-btn"><Mail className="h-4 w-4 mr-2" /> Inviami un riepilogo</Button>
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="deadlines-add-btn"><Plus className="h-4 w-4 mr-2" /> Nuova scadenza</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl border bg-white p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList><TabsTrigger value="open" data-testid="deadlines-tab-open">Da fare</TabsTrigger><TabsTrigger value="done" data-testid="deadlines-tab-done">Completate</TabsTrigger></TabsList>
            </Tabs>
            <ProfileSelect value={filter} onChange={setFilter} profiles={profiles} allowAll testId="deadlines-profile-filter" />
          </div>
          {day && <button className="mb-3 text-sm text-[#1E3A8A]" onClick={() => setDay(undefined)} data-testid="deadlines-clear-day">Mostra tutte (filtro: {fmtDate(day)})</button>}
          {shown.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="deadlines-empty">Nessuna scadenza qui.</p>}
          <ul className="divide-y">
            {shown.map((d) => (
              <li key={d.deadline_id} className="py-4 flex items-start gap-4" data-testid={`deadline-row-${d.deadline_id}`}>
                <Checkbox checked={d.done} onCheckedChange={() => toggle(d)} className="mt-1" data-testid={`deadline-toggle-${d.deadline_id}`} />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${d.done ? "line-through text-slate-400" : ""}`}>{d.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    <span>{fmtDate(d.due_date)}</span><span>{d.category}</span><span>{profileName(profiles, d.profile_id)}</span>
                    {d.amount ? <span>{fmtEur(d.amount)}</span> : null}
                    {d.recurrence !== "none" && <span className="flex items-center gap-1"><Repeat className="h-3 w-3" />{d.recurrence === "monthly" ? "Mensile" : "Annuale"}</span>}
                  </p>
                </div>
                <Badge d={d} />
                <button onClick={() => setEdit(d)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`deadline-edit-${d.deadline_id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(d)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" data-testid={`deadline-delete-${d.deadline_id}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl border bg-white p-4 h-fit">
          <Calendar mode="single" selected={day} onSelect={setDay} locale={it} modifiers={{ due: dates }}
            modifiersClassNames={{ due: "!bg-[#E07A5F]/20 !text-[#0F172A] font-semibold" }} className="mx-auto" data-testid="deadlines-calendar" />
          <p className="px-3 pb-2 text-xs text-slate-500 flex items-center gap-2"><Check className="h-3 w-3" /> I giorni evidenziati hanno scadenze aperte.</p>
        </div>
      </div>
      <DeadlineDialog value={edit} onClose={() => setEdit(null)} onSaved={load} profiles={profiles} />
    </div>
  );
}
